// src/SpirkoSearch.jsx
// Spirko AI-Powered Company Search - Business Development
// Company discovery with AI interpretation, PDL + ATS blending, contact recommendations
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Search, MapPin, Globe, Phone, Linkedin, ExternalLink, Plus, X,
  Building, Eye, Loader2, CheckCircle, AlertCircle, TrendingUp,
  Sparkles, Wand2, Brain, Database, Star, Coins, RefreshCw,
  ArrowLeft, Target, Zap, Users, SortAsc, SortDesc,
  Briefcase, Award, Tag, Layers, ChevronDown, ChevronUp,
  BookmarkPlus, MessageSquare, Settings, BarChart3, UserCheck,
  Mail, Send, DollarSign, Filter, Hash, Download, Upload, Lock,
  ShieldCheck, ShieldAlert, Smartphone
} from 'lucide-react';

// =============================================================================
// STYLES
// =============================================================================
const styles = `
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
@keyframes slideInUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.spin { animation: spin 1s linear infinite; }
.spirko-slide-right { animation: slideInRight 0.3s ease-out; }
.spirko-slide-up { animation: slideInUp 0.3s ease-out; }
.spirko-fade-in { animation: fadeIn 0.2s ease-out; }
`;
if (typeof document !== 'undefined' && !document.getElementById('spirko-search-styles')) {
  const el = document.createElement('style');
  el.id = 'spirko-search-styles';
  el.textContent = styles;
  document.head.appendChild(el);
}

// =============================================================================
// CONFIG & HELPERS
// =============================================================================
const API_URL = process.env.REACT_APP_API_URL || 'https://kubi-backend-production.up.railway.app';
const buildApiUrl = (path) => {
  const base = API_URL.replace(/\/+$/, '');
  return base.endsWith('/api') && path.startsWith('/api/') ? base + path.substring(4) : base + path;
};

const useTheme = () => {
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('kubi_darkMode');
    return stored !== 'false';
  });
  useEffect(() => {
    const handleStorageChange = () => setDarkMode(localStorage.getItem('kubi_darkMode') !== 'false');
    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(handleStorageChange, 500);
    return () => { window.removeEventListener('storage', handleStorageChange); clearInterval(interval); };
  }, []);
  return { darkMode };
};

const getToken = () => {
  return localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('jwt') || localStorage.getItem('kubi_token') || sessionStorage.getItem('token');
};

const titleCase = (str) => {
  if (!str || typeof str !== 'string') return str || '';
  // Preserve known abbreviations
  const abbreviations = ['ai', 'ml', 'api', 'saas', 'b2b', 'b2c', 'iot', 'cxo', 'vp', 'ceo', 'cto', 'cfo', 'coo', 'chro', 'aws', 'it', 'hr', 'us', 'uk', 'usa', 'smb', 'devops'];
  const lowercase = ['a','an','the','and','but','or','for','nor','on','at','to','by','in','of','with','vs'];
  return str.split(' ').map((word, i) => {
    const lower = word.toLowerCase();
    if (abbreviations.includes(lower)) return word.toUpperCase();
    if (i > 0 && lowercase.includes(lower)) return lower;
    if (word === word.toUpperCase() && word.length <= 4) return word; // keep short all-caps
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }).join(' ');
};

// US Region groups — match backend region expansion
const US_REGIONS = {
  'Northeast': ['Connecticut', 'Delaware', 'Maine', 'Maryland', 'Massachusetts', 'New Hampshire', 'New Jersey', 'New York', 'Pennsylvania', 'Rhode Island', 'Vermont'],
  'Southeast': ['Alabama', 'Arkansas', 'Florida', 'Georgia', 'Kentucky', 'Louisiana', 'Mississippi', 'North Carolina', 'South Carolina', 'Tennessee', 'Virginia', 'West Virginia'],
  'Midwest': ['Illinois', 'Indiana', 'Iowa', 'Kansas', 'Michigan', 'Minnesota', 'Missouri', 'Nebraska', 'North Dakota', 'Ohio', 'South Dakota', 'Wisconsin'],
  'Southwest': ['Arizona', 'New Mexico', 'Oklahoma', 'Texas'],
  'West': ['California', 'Colorado', 'Idaho', 'Montana', 'Nevada', 'Oregon', 'Utah', 'Washington', 'Wyoming'],
  'Pacific Northwest': ['Oregon', 'Washington', 'Idaho'],
  'Tri-State': ['New York', 'New Jersey', 'Connecticut'],
  'New England': ['Connecticut', 'Maine', 'Massachusetts', 'New Hampshire', 'Rhode Island', 'Vermont'],
};

// Company suggestions for filter dropdowns
const COMPANY_SUGGESTIONS = {
  industry: ['Software', 'SaaS', 'Technology', 'Information Technology', 'Financial Services', 'Healthcare', 'Manufacturing', 'Consulting', 'Telecommunications', 'Real Estate', 'Retail', 'Education', 'Media', 'Insurance', 'Energy', 'Construction', 'Automotive', 'Hospitality', 'Logistics', 'Pharmaceuticals', 'Aerospace', 'Legal', 'Biotechnology', 'Fintech'],
  company_size: ['Startup', 'Small', 'SMB', 'Mid-Market', 'Enterprise', 'Large'],
  location: [
    // Region groups first
    '--- Regions ---', 'Northeast', 'Southeast', 'Midwest', 'Southwest', 'West', 'Pacific Northwest', 'Tri-State', 'New England',
    '--- States ---', 'California', 'Texas', 'New York', 'Florida', 'Illinois', 'Ohio', 'Georgia', 'North Carolina', 'Michigan',
    'Pennsylvania', 'Colorado', 'Washington', 'Arizona', 'Massachusetts', 'Virginia', 'Tennessee', 'Minnesota', 'Indiana', 'Oregon',
    '--- Canada ---', 'Ontario', 'British Columbia', 'Alberta', 'Quebec'
  ],
  tags: ['AI', 'Machine Learning', 'Cloud', 'AWS', 'Azure', 'DevOps', 'Cybersecurity', 'Blockchain', 'IoT', 'Data Analytics', 'Mobile', 'E-Commerce', 'B2B', 'B2C', 'Enterprise Software', 'Open Source', 'Digital Transformation', 'Automation'],
  contact_level: ['CXO', 'Owner', 'Partner', 'VP', 'Director', 'Manager'],
  company_type: ['Private', 'Public', 'Nonprofit', 'Government'],
  region: Object.keys(US_REGIONS)
};

// Sales methodology info
const METHODOLOGY_INFO = {
  consultative: { label: 'Consultative Selling', desc: 'Ask questions to understand the prospect\'s needs, then recommend the best solution. Great for complex or customized offerings.' },
  solution: { label: 'Solution Selling', desc: 'Focus on the prospect\'s pain points and show how your product/service solves them. Best for B2B and technical sales.' },
  challenger: { label: 'Challenger Sale', desc: 'Teach prospects something new about their business, then show why your approach is better. Works for competitive markets.' },
  relationship: { label: 'Relationship Selling', desc: 'Build trust and long-term connections with prospects. Ideal for repeat business and referral-driven industries.' },
  value: { label: 'Value-Based Selling', desc: 'Lead with the ROI and business impact. Show prospects exactly what they gain in dollars and outcomes.' }
};

// =============================================================================
// THEME COLORS - Amber/Orange for Spirko (distinct from Kubi's purple)
// =============================================================================
const useColors = () => {
  const { darkMode } = useTheme();

  if (darkMode) {
    return {
      bg: '#0f172a', bgCard: '#1e293b', bgHover: '#334155', bgInput: '#1e293b',
      bgMuted: '#334155',
      bgAccent: 'rgba(245, 158, 11, 0.15)',
      bgSuccess: 'rgba(16, 185, 129, 0.15)',
      bgWarning: 'rgba(251, 146, 60, 0.15)',
      bgDanger: 'rgba(239, 68, 68, 0.15)',
      text: '#f8fafc', textSecondary: '#cbd5e1', textMuted: '#94a3b8',
      border: '#334155', borderLight: '#475569',
      shadow: '0 4px 12px rgba(0,0,0,0.3)', shadowLg: '0 8px 24px rgba(0,0,0,0.4)',
      primary: '#f59e0b', accent: '#f97316', success: '#10b981',
      warning: '#fb923c', danger: '#ef4444',
      darkMode: true
    };
  }
  return {
    bg: '#f8fafc', bgCard: '#ffffff', bgHover: '#f1f5f9', bgInput: '#ffffff',
    bgMuted: '#f1f5f9',
    bgAccent: 'rgba(245, 158, 11, 0.08)',
    bgSuccess: 'rgba(16, 185, 129, 0.08)',
    bgWarning: 'rgba(251, 146, 60, 0.08)',
    bgDanger: 'rgba(239, 68, 68, 0.08)',
    text: '#1e293b', textSecondary: '#475569', textMuted: '#64748b',
    border: '#e2e8f0', borderLight: '#cbd5e1',
    shadow: '0 1px 3px rgba(0,0,0,0.08)', shadowLg: '0 4px 12px rgba(0,0,0,0.1)',
    primary: '#d97706', accent: '#ea580c', success: '#059669',
    warning: '#ea580c', danger: '#dc2626',
    darkMode: false
  };
};

// =============================================================================
// REUSABLE COMPONENTS
// =============================================================================
const Card = ({ children, style = {}, className = '' }) => {
  const colors = useColors();
  return (
    <div className={className} style={{
      background: colors.bgCard, borderRadius: 12,
      border: `1px solid ${colors.border}`, boxShadow: colors.shadow,
      ...style
    }}>{children}</div>
  );
};

const Button = ({ children, variant = 'primary', size = 'md', icon: Icon, loading, disabled, onClick, style = {} }) => {
  const colors = useColors();
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary': return { background: colors.primary, color: '#fff', border: 'none' };
      case 'secondary': return { background: colors.bgMuted, color: colors.text, border: `1px solid ${colors.border}` };
      case 'ghost': return { background: 'transparent', color: colors.textSecondary, border: 'none' };
      case 'spirko': return { background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`, color: '#fff', border: 'none' };
      case 'success': return { background: colors.success, color: '#fff', border: 'none' };
      case 'danger': return { background: colors.danger, color: '#fff', border: 'none' };
      default: return { background: colors.primary, color: '#fff', border: 'none' };
    }
  };
  const getSizeStyles = () => {
    switch (size) {
      case 'sm': return { padding: '6px 12px', fontSize: 12 };
      case 'lg': return { padding: '12px 24px', fontSize: 15 };
      default: return { padding: '10px 18px', fontSize: 13 };
    }
  };
  return (
    <button onClick={onClick} disabled={disabled || loading} style={{
      ...getVariantStyles(), ...getSizeStyles(),
      borderRadius: 8, fontWeight: 600, cursor: disabled || loading ? 'not-allowed' : 'pointer',
      opacity: disabled || loading ? 0.6 : 1, display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', gap: 6, transition: 'all 0.2s', whiteSpace: 'nowrap', ...style
    }}>
      {loading ? <Loader2 size={14} className="spin" /> : Icon && <Icon size={14} />}
      {children}
    </button>
  );
};

const Badge = ({ children, variant = 'default', size = 'md', onClick, style = {} }) => {
  const colors = useColors();
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    borderRadius: 16, fontWeight: 600, cursor: onClick ? 'pointer' : 'default',
    transition: 'all 0.2s',
    padding: size === 'sm' ? '3px 8px' : '4px 10px',
    fontSize: size === 'sm' ? 10 : 11
  };
  const getStyles = () => {
    switch (variant) {
      case 'ats': return { ...base, background: 'rgba(37, 99, 235, 0.1)', color: '#3b82f6' };
      case 'knine': return { ...base, background: 'rgba(245, 158, 11, 0.12)', color: '#d97706', border: '1px solid rgba(245,158,11,0.3)', fontWeight: 700 };
      case 'spirko': return { ...base, background: colors.bgAccent, color: colors.primary };
      case 'excellent': return { ...base, background: colors.bgSuccess, color: colors.success };
      case 'good': return { ...base, background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' };
      case 'fair': return { ...base, background: colors.bgWarning, color: colors.warning };
      default: return { ...base, background: colors.bgMuted, color: colors.textSecondary };
    }
  };
  return <span onClick={onClick} style={{ ...getStyles(), ...style }}>{children}</span>;
};

const CompanyAvatar = ({ name, size = 48 }) => {
  const initial = (name || '?').charAt(0).toUpperCase();
  const colorSets = [
    ['#f59e0b', '#fbbf24'], ['#f97316', '#fb923c'], ['#ef4444', '#f87171'],
    ['#06b6d4', '#22d3ee'], ['#10b981', '#34d399'], ['#1E88E5', '#a78bfa'], ['#3b82f6', '#60a5fa']
  ];
  const idx = name ? name.charCodeAt(0) % colorSets.length : 0;
  return (
    <div style={{
      width: size, height: size, borderRadius: 10,
      background: `linear-gradient(135deg, ${colorSets[idx][0]}, ${colorSets[idx][1]})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: size * 0.4, fontWeight: 700, flexShrink: 0
    }}>{initial}</div>
  );
};

const EmptyState = ({ icon: Icon, title, description, action }) => {
  const colors = useColors();
  return (
    <div style={{ textAlign: 'center', padding: 48 }}>
      {Icon && <Icon size={48} style={{ color: colors.textMuted, marginBottom: 16 }} />}
      <h3 style={{ color: colors.text, fontWeight: 600, marginBottom: 8 }}>{title}</h3>
      {description && <p style={{ color: colors.textMuted, fontSize: 14, maxWidth: 400, margin: '0 auto 16px' }}>{description}</p>}
      {action}
    </div>
  );
};

// Session storage key for persistence
const SPIRKO_STATE_KEY = 'spirko_search_state';

// =============================================================================
// KNINE MODAL — Replaces native alert/confirm/prompt with branded modals
// =============================================================================
function KNineModal({ modal, onClose }) {
  const colors = useColors();
  const [inputValue, setInputValue] = useState(modal?.defaultValue || '');

  if (!modal) return null;

  const isSuccess = modal.variant === 'success';
  const isError = modal.variant === 'error';
  const isWarning = modal.variant === 'warning';
  const iconColor = isSuccess ? colors.success : isError ? (colors.danger || '#ef4444') : isWarning ? '#f59e0b' : colors.primary;
  const IconComponent = isSuccess ? CheckCircle : isError ? AlertCircle : isWarning ? AlertCircle : CheckCircle;

  return (
    <>
      <div onMouseDown={() => { if (!modal.requireAction) onClose(null); }} className="spirko-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200 }} />
      <div className="spirko-slide-up" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1201, width: 380, maxWidth: '90vw' }}>
        <Card style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: `linear-gradient(135deg, ${iconColor}, ${colors.primary})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <IconComponent size={18} style={{ color: '#fff' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: colors.text }}>
                {modal.title || 'kNine'}
              </h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
                {modal.message}
              </p>
            </div>
            {!modal.requireAction && (
              <button onClick={() => onClose(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={16} style={{ color: colors.textMuted }} />
              </button>
            )}
          </div>

          {modal.type === 'prompt' && (
            <input
              autoFocus
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder={modal.placeholder || ''}
              onKeyDown={e => { if (e.key === 'Enter' && inputValue.trim()) onClose(inputValue); }}
              style={{
                width: '100%', padding: '10px 12px', marginBottom: 14,
                background: colors.bgInput, color: colors.text,
                border: `1px solid ${colors.border}`, borderRadius: 8,
                fontSize: 13, outline: 'none', boxSizing: 'border-box'
              }}
            />
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {modal.type === 'confirm' && (
              <Button variant="ghost" size="sm" onClick={() => onClose(false)}>Cancel</Button>
            )}
            {modal.type === 'prompt' && (
              <Button variant="ghost" size="sm" onClick={() => onClose(null)}>Cancel</Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (modal.type === 'prompt') onClose(inputValue.trim() || null);
                else if (modal.type === 'confirm') onClose(true);
                else onClose(true);
              }}
            >
              {modal.type === 'confirm' ? (modal.confirmLabel || 'Continue') : modal.type === 'prompt' ? 'Save' : 'OK'}
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function SpirkoSearch() {
  const colors = useColors();

  // Restore saved state
  const saved = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(SPIRKO_STATE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  // Search state
  const [query, setQuery] = useState(saved?.query || '');
  const [results, setResults] = useState(saved?.results || []);
  const [allResults, setAllResults] = useState(saved?.allResults || []);
  const [total, setTotal] = useState(saved?.total || 0);
  const [searched, setSearched] = useState(saved?.searched || false);
  const [searching, setSearching] = useState(false);
  const [activeSearch, setActiveSearch] = useState(saved?.activeSearch || '');

  // AI interpretation
  const [aiInterpretation, setAiInterpretation] = useState(saved?.aiInterpretation || null);
  const [filters, setFilters] = useState(saved?.filters || null);
  const [showFilters, setShowFilters] = useState(saved?.showFilters !== false); // default open
  const [expandedFilters, setExpandedFilters] = useState(saved?.expandedFilters || false);
  const [resultLimit, setResultLimit] = useState(saved?.resultLimit || 10);

  // Conversation context
  const [conversationId, setConversationId] = useState(saved?.conversationId || null);
  const [creditsRemaining, setCreditsRemaining] = useState(saved?.creditsRemaining ?? null);

  // Pagination
  const [scrollToken, setScrollToken] = useState(saved?.scrollToken || null);
  const [totalAvailable, setTotalAvailable] = useState(saved?.totalAvailable || 0);
  const [loadingMore, setLoadingMore] = useState(false);

  // Sort
  const [sortBy, setSortBy] = useState(saved?.sortBy || 'matchScore');
  const [sortDir, setSortDir] = useState(saved?.sortDir || 'desc');

  // Drawer
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Prospects & ATS import
  const [savedProspects, setSavedProspects] = useState(new Set(saved?.savedProspects || []));
  const [savingProspect, setSavingProspect] = useState(null);
  const [importedToAts, setImportedToAts] = useState(new Set(saved?.importedToAts || []));
  const [importingToAts, setImportingToAts] = useState(null);
  // Per-contact validation results keyed by contact id or name
  const [contactValidation, setContactValidation] = useState({});

  // Chat refinement
  const [chatMessage, setChatMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // Sources
  const [sources, setSources] = useState(saved?.sources || ['ats', 'knine']);
  const [sourceCounts, setSourceCounts] = useState(saved?.sourceCounts || { ats: 0, knine: 0 });
  const isTrial = (() => { try { const p = localStorage.getItem('kubi_plan') || 'free'; return !p || p === 'free' || p === 'trial' || p === 'trialing'; } catch { return true; } })();
  const [showAtsUpgradeHint, setShowAtsUpgradeHint] = useState(false);

  // Settings panel
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);

  // Client import / company browser
  const [showImportPanel, setShowImportPanel] = useState(false);
  const [importingClients, setImportingClients] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [browseCompanies, setBrowseCompanies] = useState([]);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [browsePage, setBrowsePage] = useState(1);
  const [browseTotalPages, setBrowseTotalPages] = useState(1);
  const [browseSearch, setBrowseSearch] = useState('');
  const [browseStatus, setBrowseStatus] = useState('All');
  const [browseLoading, setBrowseLoading] = useState(false);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(new Set());
  const [browseDetailCompany, setBrowseDetailCompany] = useState(null);
  const [browseDetailContacts, setBrowseDetailContacts] = useState([]);
  const [browseDetailLoading, setBrowseDetailLoading] = useState(false);
  const [atsLists, setAtsLists] = useState([]);
  const [atsListsLoading, setAtsListsLoading] = useState(false);

  // KNine branded modal (replaces native alert/confirm/prompt)
  const [knModal, setKnModal] = useState(null);
  const knModalResolveRef = useRef(null);

  const knAlert = (message, opts = {}) => {
    return new Promise(resolve => {
      knModalResolveRef.current = resolve;
      setKnModal({ type: 'alert', message, title: opts.title || 'kNine', variant: opts.variant || 'info', ...opts });
    });
  };
  const handleKnModalClose = (result) => {
    const resolve = knModalResolveRef.current;
    knModalResolveRef.current = null;
    setKnModal(null);
    if (resolve) resolve(result);
  };

  // State persistence ref
  const searchStateRef = useRef();
  searchStateRef.current = {
    query, results, allResults, total, searched, activeSearch,
    aiInterpretation, filters, showFilters, expandedFilters, resultLimit,
    conversationId, creditsRemaining, sortBy, sortDir,
    sources, sourceCounts,
    savedProspects: [...savedProspects],
    importedToAts: [...importedToAts]
  };

  // Save on unmount (covers React Router navigation away)
  useEffect(() => {
    return () => {
      try { sessionStorage.setItem(SPIRKO_STATE_KEY, JSON.stringify(searchStateRef.current)); }
      catch (e) { console.warn('[Spirko] Failed to persist state on unmount:', e.message); }
    };
  }, []);

  // Save immediately when results arrive so navigation timing can't drop them
  useEffect(() => {
    if (!searched) return;
    try { sessionStorage.setItem(SPIRKO_STATE_KEY, JSON.stringify(searchStateRef.current)); }
    catch (e) { console.warn('[Spirko] Failed to persist state on results change:', e.message); }
  }, [results, searched]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // SEARCH
  // =========================================================================
  const doSearch = async (searchQuery, isRefinement = false) => {
    const token = getToken();
    if (!token) return;

    setSearching(true);
    setActiveSearch(searchQuery);
    if (!isRefinement) {
      setResults([]);
      setAllResults([]);
      setScrollToken(null);
      setTotalAvailable(0);
    }

    try {
      const resp = await fetch(buildApiUrl('/api/spirko-search'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          query: searchQuery,
          sources,
          conversationId: isRefinement ? conversationId : undefined,
          isRefinement,
          previousContext: isRefinement && filters ? { filters } : undefined
        })
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        if (resp.status === 402) {
          knAlert('Insufficient credits for company search', { title: 'Insufficient Credits', variant: 'warning' });
          return;
        }
        throw new Error(err.error || 'Search failed');
      }

      const data = await resp.json();

      setResults(data.results || []);
      setAllResults(data.results || []);
      setTotal(data.total || 0);
      setFilters(data.filters || null);
      setAiInterpretation(data.summary || null);
      setConversationId(data.conversationId || null);
      setSourceCounts(data.sourceCounts || { ats: 0, knine: 0 });
      if (data.resultLimit) setResultLimit(data.resultLimit);
      if (data.creditsRemaining !== undefined) setCreditsRemaining(data.creditsRemaining);
      setScrollToken(data.scrollToken || null);
      setTotalAvailable(data.totalAvailable || 0);
      setSearched(true);
      // Auto-expand filters panel when results arrive
      if (data.filters) setShowFilters(true);

    } catch (err) {
      console.error('Spirko search error:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    doSearch(query.trim());
  };

  const handleLoadMore = async () => {
    if (!scrollToken || loadingMore) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoadingMore(true);
    try {
      const resp = await fetch(buildApiUrl('/api/spirko-search'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          query: activeSearch,
          sources: ['knine'],
          conversationId,
          scrollToken,
          previousContext: filters ? { filters } : undefined
        })
      });
      if (!resp.ok) throw new Error('Load more failed');
      const data = await resp.json();
      const combined = [...allResults, ...(data.results || [])];
      setResults(combined);
      setAllResults(combined);
      setScrollToken(data.scrollToken || null);
      setTotalAvailable(data.totalAvailable || totalAvailable);
    } catch (err) {
      console.error('Load more error:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // =========================================================================
  // CHAT REFINEMENT
  // =========================================================================
  const handleChat = async () => {
    if (!chatMessage.trim()) return;
    setChatLoading(true);
    const token = getToken();

    try {
      const resp = await fetch(buildApiUrl('/api/spirko-search/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          message: chatMessage.trim(),
          conversationId,
          currentFilters: filters
        })
      });

      if (resp.ok) {
        const data = await resp.json();
        setFilters(data.filters);
        setAiInterpretation(data.summary || data.message);
        setConversationId(data.conversationId);
        setChatMessage('');
        // Re-search with new filters
        doSearch(chatMessage.trim(), true);
      }
    } catch (err) {
      console.error('Spirko chat error:', err);
    } finally {
      setChatLoading(false);
    }
  };

  // =========================================================================
  // CONTACTS
  // =========================================================================
  const loadContacts = async (company) => {
    setLoadingContacts(true);
    setContacts([]);
    const token = getToken();

    try {
      const resp = await fetch(buildApiUrl('/api/spirko-search/contacts'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          companyName: company.name,
          companyDomain: company.domain || company.website,
          filters: { contact_levels: filters?.contact_levels || ['cxo', 'vp', 'director'], contact_titles: filters?.contact_titles || [] }
        })
      });

      if (resp.ok) {
        const data = await resp.json();
        setContacts(data.contacts || []);
      }
    } catch (err) {
      console.error('Contact lookup error:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  // =========================================================================
  // SAVE PROSPECT
  // =========================================================================
  const saveProspect = async (company, contact = null) => {
    const token = getToken();
    setSavingProspect(contact?.id || contact?.name || company.id || company.name);

    try {
      const resp = await fetch(buildApiUrl('/api/spirko-search/save-prospect'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ company, contact })
      });

      const data = await resp.json().catch(() => ({}));

      if (resp.ok && data.success) {
        const contactKey = contact?.id || contact?.name;
        setSavedProspects(prev => new Set([...prev, contactKey || company.id || company.name]));
        // Store validation result so ContactCard can show badge
        if (data.validation && contactKey) {
          setContactValidation(prev => ({ ...prev, [contactKey]: data.validation }));
        }
        const parts = [data.message];
        if (data.companyMessage) parts.push(data.companyMessage);
        if (data.candidateId && data.enriched) parts.push('Full enriched data, work history, and resume included.');
        if (data.validation?.verifiedEmail) parts.push(`✓ Email verified: ${data.validation.verifiedEmail.address}`);
        if (data.validation?.verifiedPhone) parts.push(`✓ Phone verified: ${data.validation.verifiedPhone.number} (${data.validation.verifiedPhone.lineType || 'valid'})`);
        if (data.creditNote) parts.push(data.creditNote);
        knAlert(parts.join('\n'), { title: 'Saved to ATS', variant: 'success' });
      } else if (resp.status === 402) {
        knAlert(data.message || 'Insufficient credits.', { title: 'Credits Required', variant: 'error' });
      } else {
        knAlert(data.error || 'Failed to save to ATS', { title: 'Error', variant: 'error' });
      }
    } catch (err) {
      console.error('Save prospect error:', err);
      knAlert('Network error — please try again.', { title: 'Error', variant: 'error' });
    } finally {
      setSavingProspect(null);
    }
  };

  // =========================================================================
  // IMPORT TO ATS
  // =========================================================================
  const importToAts = async (company) => {
    const token = getToken();
    setImportingToAts(company.id || company.name);

    try {
      const resp = await fetch(buildApiUrl('/api/spirko-search/import-to-ats'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ company })
      });

      if (resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setImportedToAts(prev => new Set([...prev, company.id || company.name]));
        knAlert(
          data.message || `Company Record for "${company.name}" has been created in your ATS${data.atsSystem ? ` (${data.atsSystem})` : ''}.`,
          { title: 'Saved to ATS', variant: 'success' }
        );
      } else {
        const err = await resp.json().catch(() => ({}));
        knAlert(err.error || 'Failed to import to ATS', { title: 'Import Error', variant: 'error' });
      }
    } catch (err) {
      console.error('Import to ATS error:', err);
    } finally {
      setImportingToAts(null);
    }
  };

  // =========================================================================
  // SORTING
  // =========================================================================
  const sortResults = (field) => {
    const newDir = sortBy === field && sortDir === 'desc' ? 'asc' : 'desc';
    setSortBy(field);
    setSortDir(newDir);

    const sorted = [...results].sort((a, b) => {
      let va = a[field], vb = b[field];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (newDir === 'asc') return va > vb ? 1 : -1;
      return va < vb ? 1 : -1;
    });
    setResults(sorted);
  };

  // =========================================================================
  // SETTINGS
  // =========================================================================
  const loadSettings = async () => {
    const token = getToken();
    try {
      const resp = await fetch(buildApiUrl('/api/spirko-search/settings'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setSettings(data.settings);
      }
    } catch (e) { console.error('Load settings error:', e); }
  };

  const saveSettings = async (newSettings) => {
    const token = getToken();
    try {
      const resp = await fetch(buildApiUrl('/api/spirko-search/settings'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ settings: newSettings })
      });
      if (resp.ok) {
        setSettings(newSettings);
        setShowSettings(false);
      }
    } catch (e) { console.error('Save settings error:', e); }
  };

  // =========================================================================
  // COMPANY BROWSER — Browse, search, select, detail, analyze
  // =========================================================================
  const browseDebounceRef = useRef(null);
  const browseInitializedRef = useRef(false);

  const loadBrowseCompanies = async (page = 1, search = '', status = 'All') => {
    const token = getToken();
    if (!token) return;
    setBrowseLoading(true);

    try {
      const params = new URLSearchParams({ page, perPage: 25 });
      if (search) params.set('search', search);
      if (status && status !== 'All') params.set('status', status);

      const resp = await fetch(buildApiUrl(`/api/spirko-search/browse-companies?${params}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (resp.ok) {
        const data = await resp.json();
        setBrowseCompanies(data.companies || []);
        setBrowseTotal(data.total || 0);
        setBrowsePage(data.page || 1);
        setBrowseTotalPages(data.totalPages || 1);
      }
    } catch (err) {
      console.error('Browse companies error:', err);
    } finally {
      setBrowseLoading(false);
    }
  };

  // Auto-load companies + lists when panel opens
  useEffect(() => {
    if (showImportPanel && !browseInitializedRef.current) {
      browseInitializedRef.current = true;
      loadBrowseCompanies(1, '', browseStatus);
      loadAtsLists();
    }
    if (!showImportPanel) {
      browseInitializedRef.current = false;
    }
  }, [showImportPanel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search-as-you-type
  useEffect(() => {
    if (!showImportPanel) return;
    if (browseDebounceRef.current) clearTimeout(browseDebounceRef.current);
    browseDebounceRef.current = setTimeout(() => {
      loadBrowseCompanies(1, browseSearch, browseStatus);
    }, 350);
    return () => { if (browseDebounceRef.current) clearTimeout(browseDebounceRef.current); };
  }, [browseSearch, browseStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCompanyDetail = async (companyId) => {
    const token = getToken();
    if (!token) return;
    setBrowseDetailLoading(true);
    setBrowseDetailContacts([]);

    try {
      const resp = await fetch(buildApiUrl(`/api/spirko-search/company-details/${companyId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (resp.ok) {
        const data = await resp.json();
        setBrowseDetailCompany(data.company);
        setBrowseDetailContacts(data.contacts || []);
      }
    } catch (err) {
      console.error('Company detail error:', err);
    } finally {
      setBrowseDetailLoading(false);
    }
  };

  const loadAtsLists = async () => {
    const token = getToken();
    if (!token) return;
    setAtsListsLoading(true);

    try {
      const resp = await fetch(buildApiUrl('/api/spirko-search/ats-lists'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setAtsLists(data.lists || []);
      }
    } catch (err) {
      console.error('ATS lists error:', err);
    } finally {
      setAtsListsLoading(false);
    }
  };

  const toggleCompanySelection = (companyId) => {
    setSelectedCompanyIds(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedCompanyIds(prev => {
      const next = new Set(prev);
      browseCompanies.forEach(c => next.add(c.id));
      return next;
    });
  };

  const deselectAll = () => setSelectedCompanyIds(new Set());

  const analyzeSelectedCompanies = async () => {
    const token = getToken();
    if (!token || selectedCompanyIds.size === 0) return;
    setImportingClients(true);
    setImportResult(null);

    try {
      const resp = await fetch(buildApiUrl('/api/spirko-search/import-clients'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ companyIds: [...selectedCompanyIds] })
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'Analysis failed');
      }

      const data = await resp.json();
      setImportResult(data);
      if (data.searchSuggestion) setQuery(data.searchSuggestion);
    } catch (err) {
      console.error('Analyze companies error:', err);
      setImportResult({ error: err.message });
    } finally {
      setImportingClients(false);
    }
  };

  const analyzeByStatus = async (statusFilter) => {
    const token = getToken();
    if (!token) return;
    setImportingClients(true);
    setImportResult(null);

    try {
      const resp = await fetch(buildApiUrl('/api/spirko-search/import-clients'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: statusFilter || 'Client' })
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'Analysis failed');
      }

      const data = await resp.json();
      setImportResult(data);
      if (data.searchSuggestion) setQuery(data.searchSuggestion);
    } catch (err) {
      console.error('Analyze by status error:', err);
      setImportResult({ error: err.message });
    } finally {
      setImportingClients(false);
    }
  };

  // Open company detail
  const openCompany = (company) => {
    setSelectedCompany(company);
    setContacts([]);
    loadContacts(company);
  };

  // =========================================================================
  // RENDER - SEARCH BAR
  // =========================================================================
  const renderSearchBar = () => (
    <Card style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{
          background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
          borderRadius: 8, padding: 6, display: 'flex'
        }}>
          <Target size={18} color="#fff" />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: colors.text }}>Cooper AI Company Search</div>
          <div style={{ fontSize: 12, color: colors.textMuted }}>Find target companies with AI-powered discovery</div>
        </div>
        <div style={{ flex: 1 }} />
        {creditsRemaining !== null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: colors.textMuted }}>
            <Coins size={14} /> {creditsRemaining} credits
          </div>
        )}
        <Button variant="ghost" size="sm" icon={Download} onClick={() => setShowImportPanel(true)}>
          Import Clients
        </Button>
        <Button variant="ghost" size="sm" icon={Settings} onClick={() => { loadSettings(); setShowSettings(true); }}>
          BD Settings
        </Button>
      </div>

      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: colors.textMuted }} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Find SaaS companies in Texas with 50-500 employees..."
            style={{
              width: '100%', padding: '12px 12px 12px 38px',
              background: colors.bgInput, color: colors.text,
              border: `1px solid ${colors.border}`, borderRadius: 10,
              fontSize: 14, outline: 'none', boxSizing: 'border-box'
            }}
            onFocus={e => e.target.style.borderColor = colors.primary}
            onBlur={e => e.target.style.borderColor = colors.border}
          />
        </div>
        <Button variant="spirko" icon={searching ? Loader2 : Sparkles} loading={searching} disabled={!query.trim()}>
          Search
        </Button>
      </form>

      {/* Source toggles — equal size, animated, ATS Companies locked on trial */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 4, padding: 3, borderRadius: 8, background: colors.bgMuted }}>
        <style>{`
          @keyframes kn-ats-pulse {
            0%,100% { box-shadow: 0 0 0 0 rgba(10,102,194,0.35); }
            50%      { box-shadow: 0 0 0 5px rgba(10,102,194,0); }
          }
          @keyframes kn-knine-pulse {
            0%,100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.35); }
            50%      { box-shadow: 0 0 0 5px rgba(16,185,129,0); }
          }
          .kn-pill-ats   { animation: kn-ats-pulse   2.4s ease-in-out infinite; }
          .kn-pill-knine { animation: kn-knine-pulse  2.4s ease-in-out infinite; animation-delay: 0.8s; }
        `}</style>
        {[
          { key: 'ats',   label: 'ATS', icon: Database, locked: true,  upgradeMsg: 'Upgrade your account to search across your ATS or CRM companies' },
          { key: 'knine', label: 'kNine',          icon: Globe,    locked: false, upgradeMsg: '' }
        ].map(({ key, label, icon: Icon, locked, upgradeMsg }) => {
          const isLocked  = isTrial && locked;
          const isActive  = sources.includes(key);
          const pillClass = isActive && !isLocked ? (key === 'ats' ? 'kn-pill-ats' : 'kn-pill-knine') : '';
          return (
            <div key={key} style={{ position: 'relative' }}>
              <button
                className={pillClass}
                onClick={() => {
                  if (isLocked) { setShowAtsUpgradeHint(h => !h); return; }
                  setSources(prev => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]);
                }}
                title={isLocked ? upgradeMsg : `Toggle ${label}`}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  padding: '6px 0', width: 86,
                  borderRadius: 6, fontSize: 12, fontWeight: 600, border: 'none',
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  background: isLocked ? 'transparent' : (isActive ? colors.bgAccent : 'transparent'),
                  color:      isLocked ? colors.textMuted : (isActive ? colors.primary : colors.textMuted),
                  opacity: isLocked ? 0.5 : 1, transition: 'all 0.2s'
                }}
              >
                <Icon size={13} />
                {label}
                {isLocked ? <Lock size={10} /> : (isActive && <CheckCircle size={11} />)}
              </button>
              {showAtsUpgradeHint && isLocked && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 8px)', left: '50%',
                  transform: 'translateX(-50%)', zIndex: 200,
                  background: '#1e293b', color: '#fff', fontSize: 12, fontWeight: 500,
                  padding: '10px 14px', borderRadius: 10, whiteSpace: 'nowrap',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  border: '1px solid rgba(99,102,241,0.4)',
                }}>
                  🔒 {upgradeMsg}
                  <br />
                  <a href="mailto:support@knine.ai?subject=Upgrade" style={{ fontSize: 11, color: '#42A5F5', textDecoration: 'none' }}>
                    Contact us to upgrade →
                  </a>
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>
    </Card>
  );

  // =========================================================================
  // RENDER - AI FILTERS PANEL (always visible with all criteria)
  // =========================================================================
  const renderFilters = () => {
    if (!filters) return null;

    const filterGroups = [
      { key: 'industries', label: 'Industries', icon: Building, suggestKey: 'industry', weightKey: 'industry' },
      { key: 'company_sizes', label: 'Company Size', icon: Users, suggestKey: 'company_size', weightKey: 'company_size' },
      { key: 'locations', label: 'Locations', icon: MapPin, suggestKey: 'location', isLocation: true, weightKey: 'location' },
      { key: 'tags', label: 'Tags / Technology', icon: Tag, suggestKey: 'tags', weightKey: 'tags' },
      { key: 'contact_levels', label: 'Contact Levels', icon: UserCheck, suggestKey: 'contact_level' },
      { key: 'contact_titles', label: 'Contact Titles', icon: Briefcase }
    ];

    // Additional criteria row
    const extraFilters = [
      { key: 'company_type', label: 'Company Type', value: filters.company_type, suggestKey: 'company_type' },
      { key: 'min_employees', label: 'Min Employees', value: filters.min_employees, isNumber: true },
      { key: 'max_employees', label: 'Max Employees', value: filters.max_employees, isNumber: true },
      { key: 'founded_after', label: 'Founded After', value: filters.founded_after, isNumber: true },
      { key: 'founded_before', label: 'Founded Before', value: filters.founded_before, isNumber: true },
      { key: 'recent_funding', label: 'Recent Funding', value: filters.recent_funding, isToggle: true },
    ];

    const activeWeightLabel = (w) => {
      if (w === 'required') return { text: 'Required', color: colors.danger };
      if (w === 'preferred') return { text: 'Preferred', color: colors.primary };
      return { text: 'Bonus', color: colors.textMuted };
    };

    // Cycle weight on click
    const cycleWeight = (weightKey) => {
      const order = ['required', 'preferred', 'nice_to_have'];
      const current = filters.criteria_weights?.[weightKey] || 'preferred';
      const idx = order.indexOf(current);
      const next = order[(idx + 1) % order.length];
      setFilters(prev => ({
        ...prev,
        criteria_weights: { ...(prev.criteria_weights || {}), [weightKey]: next }
      }));
    };

    return (
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: showFilters ? 14 : 0, cursor: 'pointer' }}
             onClick={() => setShowFilters(!showFilters)}>
          <Wand2 size={16} style={{ color: colors.primary }} />
          <span style={{ fontWeight: 600, fontSize: 13, color: colors.text }}>AI Optimized Criteria</span>
          <span style={{ fontSize: 12, color: colors.textMuted, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {aiInterpretation}
          </span>
          {showFilters ? <ChevronUp size={16} style={{ color: colors.textMuted }} /> : <ChevronDown size={16} style={{ color: colors.textMuted }} />}
        </div>

        {showFilters && (
          <>
            {/* Main filter groups as chips */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
              {filterGroups.map(({ key, label, icon: Icon, suggestKey, isLocation, weightKey }) => {
                const values = filters[key] || [];
                const displayValues = isLocation
                  ? values.map(l => l.region || l.locality || l.country || JSON.stringify(l))
                  : values;

                const weight = weightKey ? filters.criteria_weights?.[weightKey] : null;
                const wInfo = weight ? activeWeightLabel(weight) : null;

                return (
                  <div key={key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                      <Icon size={12} style={{ color: colors.primary }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
                      {wInfo && (
                        <span
                          onClick={(e) => { e.stopPropagation(); cycleWeight(weightKey); }}
                          style={{ fontSize: 9, fontWeight: 700, color: wInfo.color, marginLeft: 'auto', cursor: 'pointer', padding: '1px 5px', borderRadius: 4, background: `${wInfo.color}15`, textTransform: 'uppercase' }}
                          title="Click to cycle: required / preferred / bonus"
                        >
                          {wInfo.text}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {displayValues.map((val, i) => (
                        <Badge key={i} variant="spirko" size="sm" onClick={() => {
                          if (isLocation) {
                            setFilters(prev => ({ ...prev, [key]: prev[key].filter((_, idx) => idx !== i) }));
                          } else {
                            setFilters(prev => ({ ...prev, [key]: prev[key].filter(v => v !== val) }));
                          }
                        }}>
                          {titleCase(String(val))} <X size={10} />
                        </Badge>
                      ))}
                      {displayValues.length === 0 && (
                        <span style={{ fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}>None set</span>
                      )}
                      <FilterAddButton filterKey={key} suggestKey={suggestKey} isLocation={isLocation}
                        onAdd={(val) => {
                          if (isLocation) {
                            setFilters(prev => ({ ...prev, [key]: [...(prev[key] || []), { region: val }] }));
                          } else {
                            setFilters(prev => ({ ...prev, [key]: [...new Set([...(prev[key] || []), val])] }));
                          }
                        }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Extra single-value criteria */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${colors.border}` }}>
              {extraFilters.map(({ key, label, value, isNumber, isToggle, suggestKey }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted }}>{label}:</span>
                  {isToggle ? (
                    <button
                      onClick={() => setFilters(prev => ({ ...prev, [key]: !prev[key] }))}
                      style={{
                        padding: '3px 10px', borderRadius: 16, fontSize: 11, fontWeight: 600,
                        border: 'none', cursor: 'pointer',
                        background: value ? colors.bgSuccess : colors.bgMuted,
                        color: value ? colors.success : colors.textMuted,
                        transition: 'all 0.2s'
                      }}
                    >
                      {value ? 'Yes' : 'No'}
                    </button>
                  ) : value ? (
                    <Badge variant="spirko" size="sm" onClick={() => setFilters(prev => ({ ...prev, [key]: null }))}>
                      {titleCase(String(value))} <X size={10} />
                    </Badge>
                  ) : (
                    <input
                      placeholder={isNumber ? '—' : 'Any'}
                      style={{
                        width: isNumber ? 60 : 80, padding: '3px 6px', fontSize: 11,
                        border: `1px solid ${colors.border}`, borderRadius: 6,
                        background: colors.bgInput, color: colors.text, outline: 'none'
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          setFilters(prev => ({ ...prev, [key]: isNumber ? parseInt(e.target.value) : e.target.value.trim() }));
                          e.target.value = '';
                        }
                      }}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Re-search button after filter edits */}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <Button variant="spirko" size="sm" icon={RefreshCw} onClick={() => doSearch(activeSearch || query, true)}>
                Apply Filters
              </Button>
            </div>
          </>
        )}

        {/* Chat refinement */}
        {searched && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12, borderTop: `1px solid ${colors.border}`, paddingTop: 12 }}>
            <input
              value={chatMessage}
              onChange={e => setChatMessage(e.target.value)}
              placeholder="Refine: also include fintech, only east coast, larger companies..."
              style={{
                flex: 1, padding: '8px 12px', background: colors.bgInput, color: colors.text,
                border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 13, outline: 'none'
              }}
              onKeyDown={e => e.key === 'Enter' && handleChat()}
            />
            <Button variant="spirko" size="sm" icon={MessageSquare} loading={chatLoading} onClick={handleChat} disabled={!chatMessage.trim()}>
              Refine
            </Button>
          </div>
        )}
      </Card>
    );
  };

  // =========================================================================
  // RENDER - FILTER ADD BUTTON WITH SUGGESTIONS
  // =========================================================================
  const FilterAddButton = ({ filterKey, suggestKey, isLocation, onAdd }) => {
    const [open, setOpen] = useState(false);
    const [inputVal, setInputVal] = useState('');
    const ref = useRef(null);

    const suggestions = suggestKey ? (COMPANY_SUGGESTIONS[suggestKey] || []) : [];
    const filtered = inputVal
      ? suggestions.filter(s => !s.startsWith('---') && s.toLowerCase().includes(inputVal.toLowerCase()))
      : suggestions.filter(s => !s.startsWith('---')).slice(0, 12);

    useEffect(() => {
      const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    if (!open) {
      return (
        <button onClick={() => setOpen(true)} style={{
          display: 'flex', alignItems: 'center', gap: 2, padding: '3px 8px',
          borderRadius: 16, fontSize: 11, fontWeight: 600, border: `1px dashed ${colors.border}`,
          background: 'transparent', color: colors.textMuted, cursor: 'pointer'
        }}>
          <Plus size={10} /> Add
        </button>
      );
    }

    return (
      <div ref={ref} style={{ position: 'relative' }}>
        <input
          autoFocus
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && inputVal.trim()) {
              onAdd(inputVal.trim().toLowerCase());
              setInputVal('');
              setOpen(false);
            }
            if (e.key === 'Escape') setOpen(false);
          }}
          style={{
            padding: '4px 8px', fontSize: 11, borderRadius: 8, width: 130,
            border: `1px solid ${colors.primary}`, background: colors.bgInput,
            color: colors.text, outline: 'none'
          }}
          placeholder={`Add ${filterKey}...`}
        />
        {filtered.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, zIndex: 20,
            background: colors.bgCard, border: `1px solid ${colors.border}`,
            borderRadius: 8, boxShadow: colors.shadowLg, maxHeight: 180, overflowY: 'auto',
            marginTop: 4, minWidth: 160
          }}>
            {filtered.map(s => {
              const isRegion = US_REGIONS[s];
              return (
                <div key={s}
                  onClick={() => { onAdd(s); setInputVal(''); setOpen(false); }}
                  style={{
                    padding: '6px 10px', fontSize: 12, color: isRegion ? colors.primary : colors.text,
                    cursor: 'pointer', transition: 'background 0.1s',
                    fontWeight: isRegion ? 600 : 400
                  }}
                  onMouseEnter={e => e.target.style.background = colors.bgHover}
                  onMouseLeave={e => e.target.style.background = 'transparent'}
                >
                  {isRegion && <MapPin size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
                  {titleCase(s)}
                  {isRegion && <span style={{ fontSize: 10, color: colors.textMuted, marginLeft: 4 }}>({US_REGIONS[s].length} states)</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // =========================================================================
  // RENDER - RESULTS
  // =========================================================================
  const renderResults = () => {
    if (searching) {
      return (
        <Card style={{ padding: 40, textAlign: 'center' }}>
          <Loader2 size={32} className="spin" style={{ color: colors.primary, marginBottom: 12 }} />
          <div style={{ fontWeight: 600, color: colors.text, marginBottom: 4 }}>Searching companies...</div>
          <div style={{ fontSize: 13, color: colors.textMuted }}>AI is analyzing your query and finding the best matches</div>
        </Card>
      );
    }

    if (!searched) {
      return (
        <EmptyState
          icon={Target}
          title="Discover Target Companies"
          description="Use natural language to find companies. Try: 'SaaS companies in California with 100-500 employees' or 'Healthcare startups in the midwest'"
        />
      );
    }

    if (results.length === 0) {
      return (
        <EmptyState icon={Building} title="No companies found" description="Try broadening your search criteria or using different industry terms" />
      );
    }

    return (
      <div>
        {/* Results header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: colors.text }}>
              {results.length} compan{results.length === 1 ? 'y' : 'ies'}
            </span>
            <span style={{ fontSize: 11, color: colors.textMuted }}>(limit: {resultLimit})</span>
            {sourceCounts.ats > 0 && <Badge variant="ats" size="sm"><Database size={10} /> ATS: {sourceCounts.ats}</Badge>}
            {sourceCounts.knine > 0 && <Badge variant="knine" size="sm"><Globe size={10} /> kNine: {sourceCounts.knine}</Badge>}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <Button variant="ghost" size="sm" icon={sortDir === 'desc' ? SortDesc : SortAsc}
              onClick={() => sortResults('matchScore')}>
              Score
            </Button>
            <Button variant="ghost" size="sm" icon={Building}
              onClick={() => sortResults('name')}>
              Name
            </Button>
          </div>
        </div>

        {/* Company cards */}
        {results.map((company, idx) => (
          <CompanyCard key={company.id || idx} company={company}
            onView={() => openCompany(company)}
            onSave={() => saveProspect(company)}
            onImport={() => importToAts(company)}
            isSaved={savedProspects.has(company.id || company.name)}
            isSaving={savingProspect === (company.id || company.name)}
            isImported={importedToAts.has(company.id || company.name)}
            isImporting={importingToAts === (company.id || company.name)}
            colors={colors}
          />
        ))}

        {/* Load More */}
        {scrollToken && (
          <div style={{ textAlign: 'center', padding: '20px 0 8px' }}>
            <div style={{ fontSize: 12, color: colors.muted, marginBottom: 10 }}>
              Showing {results.length.toLocaleString()} of {totalAvailable.toLocaleString()} matching companies
            </div>
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              style={{ padding: '10px 28px', borderRadius: 10, border: `1px solid ${colors.border}`, background: loadingMore ? colors.surface : colors.accent, color: loadingMore ? colors.muted : '#fff', fontSize: 14, fontWeight: 600, cursor: loadingMore ? 'default' : 'pointer', transition: 'all .15s' }}
            >
              {loadingMore ? 'Loading…' : `Load 10 More`}
            </button>
          </div>
        )}
      </div>
    );
  };

  // =========================================================================
  // RENDER - COMPANY DRAWER
  // =========================================================================
  const renderDrawer = () => {
    if (!selectedCompany) return null;
    const c = selectedCompany;

    return (
      <div className="kubi-drawer" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 480, maxWidth: '100vw',
        background: colors.bgCard, borderLeft: `1px solid ${colors.border}`,
        boxShadow: colors.shadowLg, zIndex: 1000, display: 'flex', flexDirection: 'column',
        overflow: 'hidden'
      }} className="spirko-slide-right">
        {/* Drawer header */}
        <div style={{ padding: 16, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setSelectedCompany(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted }}>
            <ArrowLeft size={20} />
          </button>
          <CompanyAvatar name={c.name} size={40} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: colors.text }}>{c.name}</div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>{titleCase(c.industry || 'Unknown Industry')}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {c.source === 'knine' && !importedToAts.has(c.id || c.name) && (
              <Button variant="secondary" size="sm" icon={Upload}
                loading={importingToAts === (c.id || c.name)}
                onClick={() => importToAts(c)}>
                Import
              </Button>
            )}
            {importedToAts.has(c.id || c.name) && (
              <Badge variant="excellent"><CheckCircle size={12} /> Imported</Badge>
            )}
            {!savedProspects.has(c.id || c.name) ? (
              <Button variant="spirko" size="sm" icon={BookmarkPlus}
                loading={savingProspect === (c.id || c.name)}
                onClick={() => saveProspect(c)}>
                Save
              </Button>
            ) : (
              <Badge variant="excellent"><CheckCircle size={12} /> Saved</Badge>
            )}
          </div>
        </div>

        {/* Drawer body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {/* Company details */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: colors.text }}>Company Details</span>
              {c.source === 'knine' && !importedToAts.has(c.id || c.name) && (
                <Button variant="secondary" size="sm" icon={Upload}
                  loading={importingToAts === (c.id || c.name)}
                  onClick={() => importToAts(c)}>
                  Import to ATS
                </Button>
              )}
              {importedToAts.has(c.id || c.name) && (
                <Badge variant="excellent"><CheckCircle size={12} /> Imported to ATS</Badge>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {c.location && <DetailItem icon={MapPin} label="Location" value={titleCase(c.location)} colors={colors} />}
              {c.employeeCount > 0 && <DetailItem icon={Users} label="Employees" value={c.employeeCount.toLocaleString()} colors={colors} />}
              {c.size && <DetailItem icon={Layers} label="Size" value={titleCase(c.size)} colors={colors} />}
              {c.founded && <DetailItem icon={BarChart3} label="Founded" value={c.founded} colors={colors} />}
              {c.type && <DetailItem icon={Building} label="Type" value={titleCase(c.type)} colors={colors} />}
              {c.inferredRevenue && <DetailItem icon={DollarSign} label="Revenue" value={titleCase(String(c.inferredRevenue))} colors={colors} />}
              {c.phone && <DetailItem icon={Phone} label="Phone" value={c.phone} colors={colors} />}
              {c.status && <DetailItem icon={Tag} label="Status" value={c.status} colors={colors} />}
            </div>
            {c.description && (
              <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 10, lineHeight: 1.5 }}>{c.description}</p>
            )}
            {c.tags?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
                {c.tags.slice(0, 12).map(t => <Badge key={t} size="sm">{titleCase(t)}</Badge>)}
              </div>
            )}
            {/* Links */}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              {c.website && (
                <a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: colors.primary, textDecoration: 'none' }}>
                  <Globe size={14} /> Website
                </a>
              )}
              {c.linkedinUrl && (
                <a href={c.linkedinUrl.startsWith('http') ? c.linkedinUrl : `https://${c.linkedinUrl}`} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#0a66c2', textDecoration: 'none' }}>
                  <Linkedin size={14} /> LinkedIn
                </a>
              )}
            </div>
          </div>

          {/* Match analysis */}
          <div style={{ marginBottom: 20, padding: 12, borderRadius: 8, background: colors.bgAccent }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Brain size={14} style={{ color: colors.primary }} />
              <span style={{ fontWeight: 600, fontSize: 13, color: colors.text }}>Match Analysis</span>
              <Badge variant={c.matchLevel === 'Excellent' ? 'excellent' : c.matchLevel === 'Good' ? 'good' : 'fair'} size="sm">
                {c.matchScore}% {c.matchLevel}
              </Badge>
            </div>
            {c.matchReasons?.map((r, i) => (
              <div key={i} style={{ fontSize: 12, color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                <CheckCircle size={10} style={{ color: colors.success }} /> {r}
              </div>
            ))}
          </div>

          {/* Recommended Contacts */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <UserCheck size={14} style={{ color: colors.primary }} />
              <span style={{ fontWeight: 600, fontSize: 13, color: colors.text }}>Recommended Contacts</span>
              {loadingContacts && <Loader2 size={14} className="spin" style={{ color: colors.textMuted }} />}
              {!loadingContacts && contacts.length > 0 && (
                <span style={{ fontSize: 11, color: colors.textMuted }}>{contacts.length} found</span>
              )}
            </div>

            {loadingContacts && contacts.length === 0 && (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <Loader2 size={24} className="spin" style={{ color: colors.primary }} />
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>Finding decision makers...</div>
              </div>
            )}

            {!loadingContacts && contacts.length === 0 && (
              <div style={{ textAlign: 'center', padding: 16, color: colors.textMuted, fontSize: 13 }}>
                No contacts found for this company
              </div>
            )}

            {contacts.map((contact, i) => (
              <ContactCard key={contact.id || i} contact={contact} colors={colors}
                validation={contactValidation[contact.id || contact.name] || null}
                isSaving={savingProspect === (contact.id || contact.name)}
                isSaved={savedProspects.has(contact.id || contact.name)}
                onSaveWithContact={() => saveProspect(c, contact)} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  // =========================================================================
  // RENDER - SETTINGS PANEL
  // =========================================================================
  const renderSettings = () => {
    if (!showSettings) return null;

    return (
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '100vw',
        background: colors.bgCard, borderLeft: `1px solid ${colors.border}`,
        boxShadow: colors.shadowLg, zIndex: 1001, display: 'flex', flexDirection: 'column'
      }} className="spirko-slide-right">
        <div style={{ padding: 16, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setShowSettings(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted }}>
            <ArrowLeft size={20} />
          </button>
          <Settings size={18} style={{ color: colors.primary }} />
          <span style={{ fontWeight: 700, fontSize: 16, color: colors.text }}>Cooper Settings</span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {!settings ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Loader2 size={24} className="spin" style={{ color: colors.primary }} /></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Product/Service — "What do you sell?" */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>What do you sell?</label>
                <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 8px 0' }}>
                  Describe your product or service. Cooper AI will use this to recommend the right companies and contacts to target.
                </p>
                <textarea
                  value={settings.productOrService || ''}
                  onChange={e => setSettings(prev => ({ ...prev, productOrService: e.target.value }))}
                  placeholder="e.g., We sell industrial forklifts and warehouse automation equipment to distribution centers and manufacturing plants..."
                  rows={3}
                  style={{
                    width: '100%', padding: '8px 12px', background: colors.bgInput, color: colors.text,
                    border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 13, outline: 'none',
                    resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit'
                  }}
                />
              </div>

              <SettingsSection label="Target Industries" colors={colors}
                values={settings.targetIndustries || []}
                suggestKey="industry"
                onChange={vals => setSettings(prev => ({ ...prev, targetIndustries: vals }))} />

              <SettingsSection label="Company Sizes" colors={colors}
                values={settings.companySizes || []}
                suggestKey="company_size"
                onChange={vals => setSettings(prev => ({ ...prev, companySizes: vals }))} />

              {/* Preferred Regions — multi-select US regions */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Preferred Regions</label>
                <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 8px 0' }}>
                  Select US/Canada regions. Searches will default to these territories.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {Object.keys(US_REGIONS).map(region => {
                    const isActive = (settings.preferredRegions || []).includes(region);
                    return (
                      <button key={region}
                        onClick={() => {
                          const current = settings.preferredRegions || [];
                          setSettings(prev => ({
                            ...prev,
                            preferredRegions: isActive ? current.filter(r => r !== region) : [...current, region]
                          }));
                        }}
                        style={{
                          padding: '6px 12px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                          border: 'none', cursor: 'pointer',
                          background: isActive ? colors.bgAccent : colors.bgMuted,
                          color: isActive ? colors.primary : colors.textMuted,
                          transition: 'all 0.2s'
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MapPin size={11} /> {region}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {(settings.preferredRegions || []).length > 0 && (
                  <div style={{ fontSize: 11, color: colors.textMuted, padding: '6px 10px', background: colors.bgMuted, borderRadius: 8 }}>
                    States: {(settings.preferredRegions || []).flatMap(r => US_REGIONS[r] || []).filter((v, i, a) => a.indexOf(v) === i).join(', ')}
                  </div>
                )}
              </div>

              <SettingsSection label="Preferred Locations (specific)" colors={colors}
                values={settings.preferredLocations || []}
                suggestKey="location"
                onChange={vals => setSettings(prev => ({ ...prev, preferredLocations: vals }))} />

              <SettingsSection label="Target Contact Levels" colors={colors}
                values={settings.targetContactLevels || []}
                suggestKey="contact_level"
                onChange={vals => setSettings(prev => ({ ...prev, targetContactLevels: vals }))} />

              {/* Sales Methodology with descriptions */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>Sales Methodology</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {Object.entries(METHODOLOGY_INFO).map(([key, { label, desc }]) => {
                    const isActive = (settings.salesMethodology || 'consultative') === key;
                    return (
                      <button key={key}
                        onClick={() => setSettings(prev => ({ ...prev, salesMethodology: key }))}
                        style={{
                          padding: '10px 14px', borderRadius: 10, textAlign: 'left',
                          border: isActive ? `2px solid ${colors.primary}` : `1px solid ${colors.border}`,
                          background: isActive ? colors.bgAccent : colors.bgMuted,
                          cursor: 'pointer', transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 13, color: isActive ? colors.primary : colors.text, marginBottom: 2 }}>{label}</div>
                        <div style={{ fontSize: 11, color: colors.textMuted, lineHeight: 1.4 }}>{desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>Deal Types</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {['direct_hire', 'contract', 'contract_to_hire', 'retained', 'msp'].map(dt => (
                    <button key={dt}
                      onClick={() => {
                        const current = settings.dealTypes || [];
                        setSettings(prev => ({
                          ...prev,
                          dealTypes: current.includes(dt) ? current.filter(d => d !== dt) : [...current, dt]
                        }));
                      }}
                      style={{
                        padding: '6px 12px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                        border: 'none', cursor: 'pointer',
                        background: (settings.dealTypes || []).includes(dt) ? colors.bgAccent : colors.bgMuted,
                        color: (settings.dealTypes || []).includes(dt) ? colors.primary : colors.textMuted
                      }}
                    >
                      {dt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>Min Match Score</label>
                <input
                  type="number" min={0} max={100}
                  value={settings.minMatchScore || 50}
                  onChange={e => setSettings(prev => ({ ...prev, minMatchScore: parseInt(e.target.value) || 50 }))}
                  style={{
                    width: 80, padding: '8px 12px', background: colors.bgInput, color: colors.text,
                    border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 13, outline: 'none'
                  }}
                />
              </div>

              <Button variant="spirko" icon={CheckCircle} onClick={() => saveSettings(settings)} style={{ marginTop: 8 }}>
                Save BD Settings
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // =========================================================================
  // RENDER - COMPANY BROWSER / IMPORT PANEL
  // =========================================================================
  const browseSearchRef = useRef(null);

  const renderImportPanel = () => {
    if (!showImportPanel) return null;

    // Company detail sub-view
    if (browseDetailCompany) {
      const dc = browseDetailCompany;
      return (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 560,
          background: colors.bgCard, borderLeft: `1px solid ${colors.border}`,
          boxShadow: colors.shadowLg, zIndex: 1001, display: 'flex', flexDirection: 'column'
        }} className="spirko-slide-right">
          <div style={{ padding: 16, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setBrowseDetailCompany(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted }}>
              <ArrowLeft size={20} />
            </button>
            <CompanyAvatar name={dc.name} size={36} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: colors.text }}>{dc.name}</div>
              <div style={{ fontSize: 11, color: colors.textMuted }}>{[dc.city, dc.state].filter(Boolean).join(', ')}</div>
            </div>
            <button
              onClick={() => toggleCompanySelection(dc.id)}
              style={{
                padding: '6px 14px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                border: 'none', cursor: 'pointer',
                background: selectedCompanyIds.has(dc.id) ? colors.bgSuccess : colors.bgMuted,
                color: selectedCompanyIds.has(dc.id) ? colors.success : colors.textMuted
              }}
            >
              {selectedCompanyIds.has(dc.id) ? 'Selected' : 'Select'}
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {browseDetailLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Loader2 size={24} className="spin" style={{ color: colors.primary }} /></div>
            ) : (
              <>
                {/* Company info grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                  {dc.industry && <DetailItem icon={Building} label="Industry" value={titleCase(dc.industry)} colors={colors} />}
                  {dc.status && <DetailItem icon={Tag} label="Status" value={dc.status} colors={colors} />}
                  {dc.phone && <DetailItem icon={Phone} label="Phone" value={dc.phone} colors={colors} />}
                  {dc.website && <DetailItem icon={Globe} label="Website" value={dc.website} colors={colors} />}
                  {dc.address && <DetailItem icon={MapPin} label="Address" value={dc.address} colors={colors} />}
                  {dc.zip && <DetailItem icon={Hash} label="Zip" value={dc.zip} colors={colors} />}
                  {dc.country && <DetailItem icon={Globe} label="Country" value={dc.country} colors={colors} />}
                </div>

                {dc.notes && (
                  <div style={{ marginBottom: 16 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Notes</span>
                    <p style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap' }}>
                      {dc.notes.substring(0, 500)}{dc.notes.length > 500 ? '...' : ''}
                    </p>
                  </div>
                )}

                {/* Associated contacts */}
                <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <UserCheck size={14} style={{ color: colors.primary }} />
                    <span style={{ fontWeight: 600, fontSize: 13, color: colors.text }}>Associated Contacts</span>
                    <span style={{ fontSize: 11, color: colors.textMuted }}>({browseDetailContacts.length})</span>
                  </div>

                  {browseDetailContacts.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 16, color: colors.textMuted, fontSize: 12 }}>
                      No contacts found for this company in ATS
                    </div>
                  )}

                  {browseDetailContacts.map((contact, i) => (
                    <div key={contact.id || i} style={{
                      border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 6,
                      display: 'flex', alignItems: 'center', gap: 10
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: `linear-gradient(135deg, #f59e0b, #fb923c)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0
                      }}>
                        {(contact.name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 12, color: colors.text }}>{contact.name}</div>
                        {contact.title && <div style={{ fontSize: 11, color: colors.textMuted }}>{titleCase(contact.title)}</div>}
                        <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                          {contact.email && (
                            <a href={`mailto:${contact.email}`} style={{ fontSize: 10, color: colors.primary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Mail size={9} /> {contact.email}
                            </a>
                          )}
                          {contact.phone && (
                            <span style={{ fontSize: 10, color: colors.textMuted, display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Phone size={9} /> {contact.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      );
    }

    // Main company browser view
    return (
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 560, maxWidth: '100vw',
        background: colors.bgCard, borderLeft: `1px solid ${colors.border}`,
        boxShadow: colors.shadowLg, zIndex: 1001, display: 'flex', flexDirection: 'column'
      }} className="spirko-slide-right">
        {/* Header */}
        <div style={{ padding: 14, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => { setShowImportPanel(false); setImportResult(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted }}>
            <ArrowLeft size={20} />
          </button>
          <Database size={18} style={{ color: colors.primary }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: colors.text }}>Company Database</div>
            <div style={{ fontSize: 11, color: colors.textMuted }}>Browse, select, and analyze your ATS companies</div>
          </div>
          {selectedCompanyIds.size > 0 && (
            <Badge variant="spirko">{selectedCompanyIds.size} selected</Badge>
          )}
        </div>

        {/* Search bar — type to search instantly */}
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: colors.textMuted }} />
            <input
              ref={browseSearchRef}
              value={browseSearch}
              onChange={e => setBrowseSearch(e.target.value)}
              placeholder="Type to search companies..."
              style={{
                width: '100%', padding: '8px 10px 8px 32px', background: colors.bgInput, color: colors.text,
                border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 12, outline: 'none', boxSizing: 'border-box'
              }}
            />
            {browseSearch && (
              <button onClick={() => setBrowseSearch('')} style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, padding: 2
              }}><X size={12} /></button>
            )}
          </div>

          {/* Status filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {['All', 'Client', 'Available', 'Vendor'].map(s => (
              <button key={s}
                onClick={() => setBrowseStatus(s)}
                style={{
                  padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                  border: 'none', cursor: 'pointer',
                  background: browseStatus === s ? colors.bgAccent : colors.bgMuted,
                  color: browseStatus === s ? colors.primary : colors.textMuted
                }}
              >
                {s}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            {browseCompanies.length > 0 && (
              <>
                <button onClick={selectAllVisible} style={{
                  padding: '3px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                  border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textMuted, cursor: 'pointer'
                }}>Select All</button>
                {selectedCompanyIds.size > 0 && (
                  <button onClick={deselectAll} style={{
                    padding: '3px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                    border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textMuted, cursor: 'pointer'
                  }}>Clear</button>
                )}
              </>
            )}
          </div>

          {/* ATS Rollup Lists — always shown as quick access */}
          {atsLists.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
              <Layers size={11} style={{ color: colors.textMuted, flexShrink: 0 }} />
              <span style={{ fontSize: 10, color: colors.textMuted }}>Lists:</span>
              {atsLists.map(list => (
                <button key={list.code}
                  onClick={() => { setBrowseSearch(list.name); }}
                  style={{
                    padding: '3px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600,
                    border: `1px solid ${colors.border}`, background: colors.bgMuted, color: colors.textSecondary,
                    cursor: 'pointer', transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = colors.bgAccent; e.currentTarget.style.color = colors.primary; }}
                  onMouseLeave={e => { e.currentTarget.style.background = colors.bgMuted; e.currentTarget.style.color = colors.textSecondary; }}
                  title={list.description || list.name}
                >
                  {list.name} {list.memberCount > 0 && <span style={{ color: colors.textMuted }}>({list.memberCount})</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Matching lists inline — show when search matches a list name */}
        {browseSearch && atsLists.filter(l => l.name.toLowerCase().includes(browseSearch.toLowerCase())).length > 0 && (
          <div style={{ padding: '8px 14px', borderBottom: `1px solid ${colors.border}`, background: colors.bgMuted }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Matching Lists</span>
            {atsLists.filter(l => l.name.toLowerCase().includes(browseSearch.toLowerCase())).map(list => (
              <div key={list.code} style={{
                padding: '6px 10px', borderRadius: 8, marginBottom: 3,
                background: colors.bgCard, border: `1px solid ${colors.border}`,
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'
              }}
                onClick={() => setBrowseSearch(list.name)}
                onMouseEnter={e => e.currentTarget.style.borderColor = colors.primary}
                onMouseLeave={e => e.currentTarget.style.borderColor = colors.border}
              >
                <Layers size={12} style={{ color: colors.primary }} />
                <span style={{ fontWeight: 600, fontSize: 12, color: colors.text, flex: 1 }}>{list.name}</span>
                {list.memberCount > 0 && <span style={{ fontSize: 10, color: colors.textMuted }}>{list.memberCount} members</span>}
                {list.description && <span style={{ fontSize: 10, color: colors.textMuted }}>{list.description.substring(0, 40)}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Analyze selected bar — sticky */}
        {selectedCompanyIds.size > 0 && (
          <div style={{
            padding: '8px 14px', borderBottom: `1px solid ${colors.border}`,
            background: colors.bgAccent, display: 'flex', alignItems: 'center', gap: 10
          }}>
            <Brain size={14} style={{ color: colors.primary }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: colors.text, flex: 1 }}>
              {selectedCompanyIds.size} compan{selectedCompanyIds.size === 1 ? 'y' : 'ies'} selected
            </span>
            <Button variant="spirko" size="sm" icon={Sparkles} loading={importingClients}
              onClick={analyzeSelectedCompanies}>
              {importingClients ? 'Analyzing...' : 'Analyze & Recommend'}
            </Button>
          </div>
        )}

        {/* Company list or Analysis results */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Analysis results (shown when available) */}
          {importResult && !importResult.error && (
            <div style={{ padding: 14, borderBottom: `1px solid ${colors.border}` }} className="spirko-slide-up">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Brain size={16} style={{ color: colors.primary }} />
                <span style={{ fontWeight: 600, fontSize: 13, color: colors.text }}>AI Analysis</span>
                <span style={{ fontSize: 11, color: colors.textMuted }}>({importResult.clientCount} companies analyzed)</span>
                <div style={{ flex: 1 }} />
                <button onClick={() => setImportResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted }}><X size={14} /></button>
              </div>

              {importResult.analysis?.insights && (
                <p style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.5, margin: '0 0 10px 0' }}>{importResult.analysis.insights}</p>
              )}

              {importResult.analysis?.likely_product_service && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase' }}>Likely product/service:</span>
                  <span style={{ fontSize: 12, color: colors.text, marginLeft: 6 }}>{importResult.analysis.likely_product_service}</span>
                </div>
              )}

              {importResult.analysis?.recommended_industries?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Recommended Industries</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    {importResult.analysis.recommended_industries.map(ind => <Badge key={ind} variant="spirko" size="sm">{titleCase(ind)}</Badge>)}
                  </div>
                </div>
              )}

              {importResult.analysis?.client_regions?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Client Regions</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    {importResult.analysis.client_regions.map(r => <Badge key={r} size="sm"><MapPin size={9} /> {titleCase(r)}</Badge>)}
                  </div>
                </div>
              )}

              {importResult.searchSuggestion && (
                <div style={{
                  padding: 10, borderRadius: 8, background: colors.bgMuted, marginTop: 8,
                  display: 'flex', alignItems: 'center', gap: 8
                }}>
                  <Sparkles size={14} style={{ color: colors.primary, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: colors.text, flex: 1 }}>{importResult.searchSuggestion}</span>
                  <Button variant="spirko" size="sm" icon={Search}
                    onClick={() => { setShowImportPanel(false); setQuery(importResult.searchSuggestion); doSearch(importResult.searchSuggestion); }}>
                    Search
                  </Button>
                </div>
              )}
            </div>
          )}

          {importResult?.error && (
            <div style={{ padding: 14 }}>
              <div style={{ padding: 12, borderRadius: 8, background: colors.bgDanger, display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={14} style={{ color: colors.danger }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: colors.danger }}>{importResult.error}</span>
                <button onClick={() => setImportResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.danger, marginLeft: 'auto' }}><X size={12} /></button>
              </div>
            </div>
          )}

          {/* Loading state */}
          {browseLoading && (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Loader2 size={24} className="spin" style={{ color: colors.primary }} />
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>Loading companies...</div>
            </div>
          )}

          {/* Empty state — no results for current filter */}
          {!browseLoading && browseCompanies.length === 0 && !importResult && (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Building size={32} style={{ color: colors.textMuted, marginBottom: 8 }} />
              <div style={{ fontSize: 13, color: colors.textMuted }}>
                {browseSearch ? `No companies matching "${browseSearch}"` : 'No companies found'}
              </div>
              <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>
                Try a different search or status filter
              </div>
            </div>
          )}

          {/* Company list */}
          {!browseLoading && browseCompanies.length > 0 && (
            <div>
              {/* Results count */}
              <div style={{ padding: '8px 14px', fontSize: 11, color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
                Showing {browseCompanies.length} of {browseTotal} companies {browseStatus !== 'All' && `(${browseStatus})`}
              </div>

              {browseCompanies.map((c) => {
                const isSelected = selectedCompanyIds.has(c.id);
                return (
                  <div key={c.id} style={{
                    padding: '10px 14px', borderBottom: `1px solid ${colors.border}`,
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: isSelected ? colors.bgAccent : 'transparent',
                    transition: 'background 0.15s', cursor: 'pointer'
                  }}
                    onClick={() => { setBrowseDetailCompany(c); loadCompanyDetail(c.id); }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = colors.bgHover; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {/* Checkbox */}
                    <div
                      onClick={(e) => { e.stopPropagation(); toggleCompanySelection(c.id); }}
                      style={{
                        width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                        border: `2px solid ${isSelected ? colors.primary : colors.border}`,
                        background: isSelected ? colors.primary : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', transition: 'all 0.15s'
                      }}
                    >
                      {isSelected && <CheckCircle size={12} color="#fff" />}
                    </div>

                    <CompanyAvatar name={c.name} size={36} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.name}
                      </div>
                      <div style={{ display: 'flex', gap: 10, fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                        {c.industry && <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}><Building size={10} /> {titleCase(c.industry)}</span>}
                        {(c.city || c.state) && <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}><MapPin size={10} /> {[c.city, c.state].filter(Boolean).join(', ')}</span>}
                      </div>
                      {c.address && <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 1 }}>{c.address}{c.zip ? `, ${c.zip}` : ''}</div>}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                      {c.status && (
                        <span style={{
                          padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                          background: c.status === 'Client' ? colors.bgSuccess : c.status === 'Vendor' ? colors.bgWarning : colors.bgMuted,
                          color: c.status === 'Client' ? colors.success : c.status === 'Vendor' ? colors.warning : colors.textMuted
                        }}>
                          {c.status}
                        </span>
                      )}
                      {c.phone && <span style={{ fontSize: 10, color: colors.textMuted }}>{c.phone}</span>}
                    </div>
                  </div>
                );
              })}

              {/* Pagination */}
              {browseTotalPages > 1 && (
                <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Button variant="ghost" size="sm" disabled={browsePage <= 1}
                    onClick={() => loadBrowseCompanies(browsePage - 1, browseSearch, browseStatus)}>
                    Prev
                  </Button>
                  <span style={{ fontSize: 12, color: colors.textMuted }}>
                    Page {browsePage} of {browseTotalPages}
                  </span>
                  <Button variant="ghost" size="sm" disabled={browsePage >= browseTotalPages}
                    onClick={() => loadBrowseCompanies(browsePage + 1, browseSearch, browseStatus)}>
                    Next
                  </Button>
                </div>
              )}

              {/* Quick analyze by status */}
              {selectedCompanyIds.size === 0 && browseStatus !== 'All' && (
                <div style={{ padding: 10, borderTop: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Button variant="secondary" size="sm" icon={Brain}
                    loading={importingClients}
                    onClick={() => analyzeByStatus(browseStatus)}>
                    Analyze All {browseStatus} Companies
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // =========================================================================
  // MAIN RENDER
  // =========================================================================
  return (
    <div style={{ position: 'relative', padding: 16 }}>
      {renderSearchBar()}
      {renderFilters()}
      {renderResults()}
      {renderDrawer()}
      {renderSettings()}
      {renderImportPanel()}

      {/* Backdrop for drawer */}
      {(selectedCompany || showSettings || showImportPanel) && (
        <div onClick={() => { setSelectedCompany(null); setShowSettings(false); setShowImportPanel(false); }}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999 }} />
      )}

      {/* KNine branded modal — replaces native alert/confirm/prompt */}
      <KNineModal modal={knModal} onClose={handleKnModalClose} />
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function CompanyCard({ company, onView, onSave, onImport, isSaved, isSaving, isImported, isImporting, colors }) {
  const c = company;
  const isKnine = c.source === 'knine';

  return (
    <div className="spirko-fade-in" style={{
      background: colors.bgCard,
      border: `1px solid ${colors.border}`,
      borderLeft: isKnine ? '3px solid #d97706' : `1px solid ${colors.border}`,
      borderRadius: 10,
      padding: 14,
      marginBottom: 8,
      cursor: 'pointer',
      transition: 'all 0.2s',
      ...(isKnine ? { background: colors.darkMode ? 'rgba(245,158,11,0.04)' : 'rgba(245,158,11,0.02)' } : {})
    }}
      onClick={onView}
      onMouseEnter={e => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.boxShadow = colors.shadow; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = isKnine ? '#d97706' : colors.border; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <CompanyAvatar name={c.name} size={44} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.name}
            </span>
            <Badge variant={c.source === 'ats' ? 'ats' : 'knine'} size="sm">
              {c.source === 'ats' ? 'ATS' : 'kNine'}
            </Badge>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: colors.textMuted, marginBottom: 6 }}>
            {c.industry && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Building size={11} /> {titleCase(c.industry)}
              </span>
            )}
            {c.location && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <MapPin size={11} /> {titleCase(c.location)}
              </span>
            )}
            {c.employeeCount > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Users size={11} /> {c.employeeCount.toLocaleString()} employees
              </span>
            )}
            {c.size && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Layers size={11} /> {titleCase(c.size)}
              </span>
            )}
            {c.website && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Globe size={11} /> {c.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
              </span>
            )}
          </div>

          {c.tags?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 4 }}>
              {c.tags.slice(0, 5).map(t => (
                <span key={t} style={{
                  padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 500,
                  background: colors.bgMuted, color: colors.textMuted
                }}>
                  {titleCase(t)}
                </span>
              ))}
              {c.tags.length > 5 && (
                <span style={{ fontSize: 10, color: colors.textMuted }}>+{c.tags.length - 5}</span>
              )}
            </div>
          )}
        </div>

        {/* Score + Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
          <Badge variant={c.matchLevel === 'Excellent' ? 'excellent' : c.matchLevel === 'Good' ? 'good' : 'fair'} size="sm">
            {c.matchScore}% {c.matchLevel}
          </Badge>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button onClick={(e) => { e.stopPropagation(); onView(); }} style={{
              padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
              border: 'none', cursor: 'pointer',
              background: colors.primary, color: '#fff'
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Eye size={11} /> View</span>
            </button>
            {isKnine && !isImported && (
              <button onClick={(e) => { e.stopPropagation(); onImport(); }} disabled={isImporting} style={{
                padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                border: `1px solid ${colors.border}`, cursor: isImporting ? 'not-allowed' : 'pointer',
                background: colors.bgMuted, color: colors.textSecondary, opacity: isImporting ? 0.6 : 1
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  {isImporting ? <Loader2 size={11} className="spin" /> : <Upload size={11} />} Import
                </span>
              </button>
            )}
            {isImported && (
              <span style={{
                padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: 'rgba(59,130,246,0.1)', color: '#3b82f6', display: 'flex', alignItems: 'center', gap: 3
              }}>
                <CheckCircle size={11} /> Imported
              </span>
            )}
            {!isSaved ? (
              <button onClick={(e) => { e.stopPropagation(); onSave(); }} disabled={isSaving} style={{
                padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                border: `1px solid ${colors.border}`, cursor: isSaving ? 'not-allowed' : 'pointer',
                background: colors.bgMuted, color: colors.textSecondary, opacity: isSaving ? 0.6 : 1
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  {isSaving ? <Loader2 size={11} className="spin" /> : <BookmarkPlus size={11} />} Save
                </span>
              </button>
            ) : (
              <span style={{
                padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: colors.bgSuccess, color: colors.success, display: 'flex', alignItems: 'center', gap: 3
              }}>
                <CheckCircle size={11} /> Saved
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactCard({ contact, colors, validation, isSaving, isSaved, onSaveWithContact }) {
  const verifiedEmail = validation?.verifiedEmail;
  const verifiedPhone = validation?.verifiedPhone;
  const isFullyVerified = verifiedEmail?.valid && verifiedPhone;

  return (
    <div style={{
      border: `1px solid ${isFullyVerified ? 'rgba(16,185,129,0.35)' : isSaved ? 'rgba(59,130,246,0.3)' : colors.border}`,
      borderRadius: 8, padding: 10, marginBottom: 6,
      display: 'flex', alignItems: 'flex-start', gap: 10,
      background: isFullyVerified ? 'rgba(16,185,129,0.03)' : 'transparent',
      transition: 'border-color 0.3s, background 0.3s'
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: `linear-gradient(135deg, #f59e0b, #fb923c)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0
      }}>
        {(contact.name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 1 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: colors.text }}>{titleCase(contact.name)}</span>
          {isFullyVerified && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 5, background: 'rgba(16,185,129,0.12)', color: '#10b981', fontSize: 9, fontWeight: 700 }}>
              <ShieldCheck size={9} /> VERIFIED
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 4 }}>{titleCase(contact.title)} {contact.seniority ? `(${titleCase(contact.seniority)})` : ''}</div>

        {/* Contact rows — show verified address when available, fall back to raw */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {(verifiedEmail?.address || contact.email) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <a href={`mailto:${verifiedEmail?.address || contact.email}`} style={{ fontSize: 11, color: colors.primary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Mail size={10} /> {verifiedEmail?.address || contact.email}
              </a>
              {verifiedEmail?.valid && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '1px 5px', borderRadius: 4, background: 'rgba(16,185,129,0.1)', color: '#10b981', fontSize: 9, fontWeight: 700 }}>
                  <ShieldCheck size={8} /> Verified
                </span>
              )}
              {verifiedEmail?.risky && (
                <span style={{ padding: '1px 5px', borderRadius: 4, background: 'rgba(245,158,11,0.1)', color: '#f59e0b', fontSize: 9, fontWeight: 700 }}>Risky</span>
              )}
              {verifiedEmail?.invalid && (
                <span style={{ padding: '1px 5px', borderRadius: 4, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 9, fontWeight: 700 }}>Invalid</span>
              )}
            </div>
          )}
          {(verifiedPhone?.number || contact.phone) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {(() => {
                const lt = verifiedPhone?.lineType;
                const PhoneIcon = lt === 'mobile' ? Smartphone : Phone;
                const color = lt === 'mobile' ? '#10b981' : lt === 'voip' ? '#f59e0b' : colors.textMuted;
                return (
                  <>
                    <span style={{ fontSize: 11, color, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <PhoneIcon size={10} /> {verifiedPhone?.number || contact.phone}
                    </span>
                    {lt && (
                      <span style={{ padding: '1px 5px', borderRadius: 4, background: `${color}18`, color, fontSize: 9, fontWeight: 700, textTransform: 'capitalize' }}>{lt}</span>
                    )}
                  </>
                );
              })()}
            </div>
          )}
          {contact.linkedin && (
            <a href={contact.linkedin} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#0a66c2', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Linkedin size={10} /> LinkedIn
            </a>
          )}
        </div>
      </div>
      <button
        onClick={onSaveWithContact}
        disabled={isSaving || isSaved}
        style={{
          padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
          border: `1px solid ${isSaved ? 'rgba(16,185,129,0.4)' : colors.border}`,
          background: isSaved ? 'rgba(16,185,129,0.1)' : colors.bgMuted,
          color: isSaved ? '#10b981' : colors.textSecondary,
          cursor: isSaving || isSaved ? 'default' : 'pointer',
          flexShrink: 0
        }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isSaving ? <Loader2 size={10} className="spin" /> : isSaved ? <CheckCircle size={10} /> : <BookmarkPlus size={10} />}
          {isSaving ? 'Saving…' : isSaved ? 'Saved' : 'Save'}
        </span>
      </button>
    </div>
  );
}

function DetailItem({ icon: Icon, label, value, colors }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
      <Icon size={12} style={{ color: colors.textMuted, flexShrink: 0 }} />
      <span style={{ color: colors.textMuted }}>{label}:</span>
      <span style={{ color: colors.text, fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function SettingsSection({ label, values, suggestKey, onChange, colors }) {
  const [inputVal, setInputVal] = useState('');
  const suggestions = suggestKey ? (COMPANY_SUGGESTIONS[suggestKey] || []) : [];
  const [showSuggestions, setShowSuggestions] = useState(false);
  const filtered = inputVal
    ? suggestions.filter(s => s.toLowerCase().includes(inputVal.toLowerCase()) && !values.includes(s))
    : suggestions.filter(s => !values.includes(s)).slice(0, 6);

  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, display: 'block', marginBottom: 6 }}>
        {label}
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
        {values.map((v, i) => (
          <span key={v + i} style={{
            padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 600,
            background: colors.bgAccent, color: colors.primary, display: 'flex', alignItems: 'center', gap: 4
          }}>
            {titleCase(v)}
            <X size={10} style={{ cursor: 'pointer' }} onClick={() => onChange(values.filter((_, idx) => idx !== i))} />
          </span>
        ))}
      </div>
      <div style={{ position: 'relative' }}>
        <input
          value={inputVal}
          onChange={e => { setInputVal(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          onKeyDown={e => {
            if (e.key === 'Enter' && inputVal.trim()) {
              onChange([...values, inputVal.trim().toLowerCase()]);
              setInputVal('');
            }
          }}
          placeholder={`Add ${label.toLowerCase()}...`}
          style={{
            width: '100%', padding: '6px 10px', background: colors.bgInput, color: colors.text,
            border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 12, outline: 'none',
            boxSizing: 'border-box'
          }}
        />
        {showSuggestions && filtered.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
            background: colors.bgCard, border: `1px solid ${colors.border}`,
            borderRadius: 8, boxShadow: colors.shadowLg, maxHeight: 150, overflowY: 'auto', marginTop: 2
          }}>
            {filtered.map(s => (
              <div key={s}
                onMouseDown={() => { onChange([...values, s]); setInputVal(''); setShowSuggestions(false); }}
                style={{ padding: '6px 10px', fontSize: 12, color: colors.text, cursor: 'pointer' }}
                onMouseEnter={e => e.target.style.background = colors.bgHover}
                onMouseLeave={e => e.target.style.background = 'transparent'}
              >
                {titleCase(s)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
